import React, { memo, useMemo, useCallback, Fragment, useEffect, useState } from 'react';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { ScenarioDrillBlockSettings } from './ScenarioDrillBlockSettings';
import { switchMap, tap } from 'rxjs/operators';
import { Container, Row } from 'reactstrap';
import { FormHTMLInline } from '@core/components/form';
import { useCourseBuilderContext } from '../../CourseBuilderPage';
import { css } from '@emotion/react';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { of } from 'rxjs';
import { useBloc } from '@core/utils/bloc';
import { ScenarioDrillBlockBloc } from './ScenarioDrillBlockBloc';
import { Button } from '@core/components/Button';
import classNames from 'classnames';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { R } from '@core/utils/r';
import './index.scss';
import { useDidMount } from '@core/utils/hooks/react';

function defineScenarioAnswerForm() {
  return formGroup({
    option: formControl<string>({ placeholder: 'Possible Answer', required: true }),
  });
}

export function defineScenarioForm() {
  return formGroup({
    scenario: formControl<string>({ placeholder: 'Scenario', required: true }),
    answers: formArray(() => defineScenarioAnswerForm()),
    correntAnswer: formControl<string>({}),
    correntAnswerIndex: formControl<number>({}),
    answer: formControl<string>({}),
  });
}
export type ScenarioForm = ReturnType<typeof defineScenarioForm>;
export type ScenarioData = FormDataType<typeof defineScenarioForm>;

function defineScenarioDrillBlockForm() {
  return formGroup({
    order: formControl<string>({
      initialValue: 'random',
    }),
    scenarios: formArray(() => defineScenarioForm()),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type ScenarioDrillWidgetFormData = FormDataType<typeof defineScenarioDrillBlockForm>;
export type ScenarioDrillWidgetForm = ReturnType<typeof defineScenarioDrillBlockForm>;

export enum ScenarioDrillBlockVariation {
  scenarioDrill = 'scenarioDrill',
}

export const ScenarioDrillBlock = memo((props: CourseBuilder.BlockProps<ScenarioDrillBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const isEditable = props.editable;
  const editable = useMemo(() => !!isEditable || !!preview, [isEditable, preview]);
  const form = useMemo(() => defineScenarioDrillBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(ScenarioDrillBlockBloc, lessonId);

  const isRandom = useMemo(() => formValue.order === 'random', [formValue.order]);

  const scenarios = useObservable(form.controls.scenarios.items$, []);
  const scenariosItems = useMemo(() => (isRandom ? R.shuffle(scenarios) : scenarios), [isRandom, scenarios]);

  const [activeIndex, setActiveIndex] = useState(-1);
  const activeScenario = useMemo(() => R.get(scenariosItems, activeIndex), [activeIndex, scenariosItems]);
  const isDrillCompleted = useMemo(
    () => activeIndex === scenarios.length && !R.some(formValue.scenarios, s => R.isEmpty(s.answer)),
    [activeIndex, formValue.scenarios, scenarios.length],
  );
  const isDrillAllCorrect = useMemo(() => R.every(formValue.scenarios, s => s.answer === s.correntAnswer), [formValue.scenarios]);
  const [haveWrongAnswer, setWrongAnswer] = useState(false);

  useDidMount(() => {
    if (props.preview && !formValue.scenarios.length) {
      form.patchValue({
        scenarios: [
          {
            scenario: 'Scenario 1',
            answers: [{ option: 'Answer 1' }, { option: 'Answer 2' }, { option: 'Answer 3' }],
            correntAnswer: 'Answer 1',
            correntAnswerIndex: 0,
            answer: '',
          },
        ],
      });
    }
  });

  const onResetDrill = useCallback(() => {
    const newScenarios = formValue.scenarios.map(s => ({ ...s, answer: '' }));
    setWrongAnswer(false);
    setActiveIndex(0);
    form.patchValue({ scenarios: isRandom ? R.shuffle(newScenarios) : newScenarios });
  }, [form, formValue.scenarios, isRandom]);

  useEffect(() => {
    if (activeIndex === -1 && scenarios.length > 0) {
      scenarios.forEach(s => s.controls.answer.setValue(''));
      scenarios.forEach(s =>
        s.controls.correntAnswer.setValue(
          s.controls.answers.value && s.controls.answers.value.length
            ? R.get(s.controls.answers.value, `${s.controls.correntAnswerIndex.value || 0}.option`) || ''
            : '',
        ),
      );
      setActiveIndex(0);
    }
  }, [activeIndex, scenarios, scenarios.length]);

  const onSubmit = useCallback(
    () => of(isDrillAllCorrect && isDrillCompleted && !haveWrongAnswer),
    [haveWrongAnswer, isDrillAllCorrect, isDrillCompleted],
  );

  useCourseForm(props.id, form, { onSubmit });

  const onNext = useCallback(
    (shouldMove: boolean) => {
      if (!activeScenario) return;
      if (props.editable || props.preview || R.isEmpty(lessonId)) return shouldMove && setActiveIndex(activeIndex + 1);
      bloc
        .onProgress(lessonId || '', {
          title: formValue.scenarios[activeIndex].scenario,
          answer: formValue.scenarios[activeIndex].answer,
          correct: formValue.scenarios[activeIndex].correntAnswer,
          status: formValue.scenarios[activeIndex].answer == formValue.scenarios[activeIndex].correntAnswer ? 'pass' : 'fail',
        })
        .pipe(tap(() => shouldMove && setActiveIndex(activeIndex + 1)))
        .subscribe();
    },
    [activeIndex, activeScenario, bloc, formValue.scenarios, lessonId, props.editable, props.preview],
  );

  useConfigureBlock({
    props,
    form,
    contentComponent: ScenarioDrillBlockSettings,
    contentProps: { form, variation },
  });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-scenario-drill', variation)} advanceForm={formValue.advance}>
        <div style={{ flex: 1 }}>
          <div>
            {!!activeScenario && (
              <ScenarioItem
                isRandom={isRandom}
                form={activeScenario}
                onNext={onNext}
                onWrongAnswer={() => setWrongAnswer(true)}
                preview={!!props.preview}
                index={activeIndex + 1}
                totalQuestions={formValue.scenarios.length}
              />
            )}
            {isDrillCompleted && isDrillAllCorrect && (
              <div>
                {!haveWrongAnswer && (
                  <div className="d-flex align-items-start mt-2 flex-column">
                    <span className="text-success mb-2">Congratulations! You have completed this drill successfully.</span>
                    <Button type="button" onClick={onResetDrill}>
                      Redo Drill
                    </Button>
                  </div>
                )}
                {haveWrongAnswer && (
                  <div className="d-flex align-items-start mt-2 flex-column">
                    <span className="text-danger mb-2">You have to get 100% correct answers in one attempt to pass this drill.</span>
                    <Button type="button" onClick={onResetDrill}>
                      Restart Drill
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </BlockContainer>
    </Container>
  );
});

interface ScenarioItemProps {
  form: ScenarioForm;
  onNext: (val: boolean) => void;
  onWrongAnswer: () => void;
  preview: boolean;
  totalQuestions: number;
  index: number;
  isRandom: boolean;
}

const ScenarioItem = memo((props: ScenarioItemProps) => {
  const { form, preview, index, totalQuestions, isRandom } = props;
  const formValue = useNonNilObservable(form.value$);
  const [shouldRetry, setShouldRetry] = useState(false);

  const onNext = useCallback(() => {
    setShouldRetry(false);
    if (shouldRetry) {
      form.patchValue({ answer: '' });
      return;
    }

    if (formValue.answer === formValue.correntAnswer) {
      props.onNext(true);
    } else {
      props.onNext(false);
      props.onWrongAnswer();
      setShouldRetry(true);
    }
  }, [form, formValue.answer, formValue.correntAnswer, props, shouldRetry]);

  return (
    <div className="mt-2 mb-2">
      <div className="mb-2 font-weight-bold pb-4">
        <span className="mr-2">
          Scenario {index}/{totalQuestions}
          <br />
        </span>
        <span>{formValue.scenario}</span>
      </div>
      <ScenarioDrillItem control={form} canChoose={!shouldRetry} isRandom={isRandom} />
      <div className="d-flex align-items-start mt-2 flex-column">
        {shouldRetry && <span className="mt-2 mb-2 text-danger">Incorrect answer, please retry...</span>}
        {!preview && (
          <Button style={{ marginTop: 15 }} type="button" onClick={onNext}>
            {shouldRetry ? 'Re-Take' : 'Next'}
          </Button>
        )}
      </div>
    </div>
  );
});

export interface ScenarioDrillItemProps {
  control: ScenarioForm;
  canChoose?: boolean;
  isRandom: boolean;
}

export const ScenarioDrillItem = memo((props: ScenarioDrillItemProps) => {
  const { control, canChoose = true, isRandom } = props;
  const answers = useObservable(control.controls.answers.items$, []);
  const answerItems = useMemo(() => (isRandom ? R.shuffle(answers) : answers), [answers, isRandom]);
  return (
    <FormRadioGroup control={control.controls.answer}>
      {answerItems.map((ctrl, index) => (
        <FormRadio key={index} value={ctrl.controls.option.value} disabled={!canChoose} noPadding label={ctrl.controls.option.value} course />
      ))}
    </FormRadioGroup>
  );
});
